import React, { useState, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PhotoIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowPathIcon,
  ScissorsIcon,
  PencilIcon,
  CheckIcon,
  AdjustmentsHorizontalIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ImageCropper from './ImageCropper';
import { renderScannerBw } from '../utils/scannerBw';

/**
 * Bildkorrekturen für die Großansicht. Sie wirken rein visuell ueber
 * CSS-Filter -- die Datei auf dem Server bleibt unverändert.
 */
interface Adjustments {
  /** Helligkeit in Prozent (100 = unverändert). */
  brightness: number;
  /** Kontrast in Prozent (100 = unverändert). */
  contrast: number;
  /** Farbsättigung in Prozent (100 = unverändert). */
  saturate: number;
  /** Komplett entfaerben. */
  grayscale: boolean;
  /** Farben umkehren (hilft bei dunklen Scans/Negativen). */
  invert: boolean;
  /**
   * Echter Schwellwert-Modus wie bei Dokumentenscannern: alles wird auf
   * Schwarz oder Weiss gezogen. CSS kennt das nicht, deshalb per SVG-Filter.
   * Die Helligkeit wirkt davor und verschiebt damit den Schwellwert.
   */
  threshold: boolean;
  /**
   * Automatischer Dokumentenmodus: Beleuchtungsausgleich + Otsu-Schwellwert.
   * Laesst sich nicht mit CSS beschreiben, deshalb rechnet ihn die Vorschau
   * auf einem Canvas und beim Speichern das Backend (identischer Ablauf).
   */
  auto: boolean;
}

/** id des SVG-Schwellwertfilters (einmal pro Dokument im DOM). */
const THRESHOLD_FILTER_ID = 'hsv-doc-threshold';

const NEUTRAL_ADJUSTMENTS: Adjustments = {
  brightness: 100,
  contrast: 100,
  saturate: 100,
  grayscale: false,
  invert: false,
  threshold: false,
  auto: false,
};

/** Fertige Voreinstellungen für typische Beleg- und Scanprobleme. */
const ADJUSTMENT_PRESETS: { key: string; label: string; values: Adjustments }[] = [
  { key: 'original', label: 'Original', values: NEUTRAL_ADJUSTMENTS },
  { key: 'auto', label: 'Auto-Scan S/W', values: { ...NEUTRAL_ADJUSTMENTS, auto: true } },
  { key: 'grau', label: 'Graustufen', values: { ...NEUTRAL_ADJUSTMENTS, grayscale: true } },
  {
    key: 'scan',
    label: 'Scan S/W',
    values: { ...NEUTRAL_ADJUSTMENTS, brightness: 108, contrast: 175, grayscale: true },
  },
  {
    key: 'dokument',
    label: 'Dokument (S/W hart)',
    values: { ...NEUTRAL_ADJUSTMENTS, brightness: 118, contrast: 120, grayscale: true, threshold: true },
  },
  { key: 'kontrast', label: 'Mehr Kontrast', values: { ...NEUTRAL_ADJUSTMENTS, brightness: 104, contrast: 140 } },
  { key: 'hell', label: 'Aufhellen', values: { ...NEUTRAL_ADJUSTMENTS, brightness: 128, contrast: 112 } },
  { key: 'dunkel', label: 'Abdunkeln', values: { ...NEUTRAL_ADJUSTMENTS, brightness: 82, contrast: 118 } },
  { key: 'farbe', label: 'Farben kräftig', values: { ...NEUTRAL_ADJUSTMENTS, saturate: 160, contrast: 112 } },
  { key: 'invert', label: 'Invertiert', values: { ...NEUTRAL_ADJUSTMENTS, invert: true } },
];

const sameAdjustments = (a: Adjustments, b: Adjustments) =>
  a.brightness === b.brightness &&
  a.contrast === b.contrast &&
  a.saturate === b.saturate &&
  a.grayscale === b.grayscale &&
  a.invert === b.invert &&
  a.threshold === b.threshold &&
  a.auto === b.auto;

/** Ein Regler im Korrektur-Panel. Modulweit, damit er beim Tippen nicht neu montiert. */
const AdjustSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}> = ({ label, value, min, max, disabled = false, onChange }) => (
  <label className={`flex items-center gap-2 ${disabled ? 'opacity-40' : ''}`}>
    <span className="w-[68px] shrink-0 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
      {label}
    </span>
    <input
      type="range"
      min={min}
      max={max}
      step={1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-primary-600 dark:bg-gray-600"
    />
    <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
      {value}%
    </span>
  </label>
);

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DocumentViewerProps {
  documentId: number;
  fileName: string;
  filePath: string;
  onClose: () => void;
  onDocumentUpdated?: () => void;
  readOnly?: boolean;
  variant?: 'fullscreen' | 'modal';
  pdfInitialScale?: 'page-fit' | 'page-width';
  sourceUrl?: string;
  allowDownload?: boolean;
}

const getDownloadFileName = (fileName: string, contentType: string) => {
  const fallbackName = fileName.trim() || 'Dokument';
  const normalizedType = contentType.split(';')[0].trim().toLowerCase();
  const extensionByType: Record<string, string> = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
  };
  const extension = extensionByType[normalizedType];
  if (!extension) return fallbackName;

  const currentExtension = fallbackName.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase();
  if (currentExtension === extension || (extension === 'jpg' && currentExtension === 'jpeg')) {
    return fallbackName;
  }

  const baseName = currentExtension ? fallbackName.slice(0, -(currentExtension.length + 1)) : fallbackName;
  return `${baseName}.${extension}`;
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  fileName,
  filePath,
  onClose,
  onDocumentUpdated,
  readOnly = false,
  variant = 'fullscreen',
  pdfInitialScale = 'page-fit',
  sourceUrl,
  allowDownload = true,
}) => {
  useContext(AuthContext);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [isMobile] = useState(window.innerWidth < 768);
  const [touchStart, setTouchStart] = useState<{ distance: number } | null>(null);
  const [initialScale, setInitialScale] = useState(1.0);
  const [showCropper, setShowCropper] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(fileName);
  const [currentFileName, setCurrentFileName] = useState(fileName);
  const [downloadContentType, setDownloadContentType] = useState('');
  const [showAdjustments, setShowAdjustments] = useState(false);
  const [adjustments, setAdjustments] = useState<Adjustments>(NEUTRAL_ADJUSTMENTS);
  /** null = noch nicht geprüft, true = PDF ohne Textebene (reiner Scan). */
  const [isScanPdf, setIsScanPdf] = useState<boolean | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  /** Fertig gerechnete Auto-S/W-Vorschau (nur Bilder). */
  const [autoPreviewUrl, setAutoPreviewUrl] = useState<string | null>(null);
  const [autoPreviewPending, setAutoPreviewPending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);

  const detectedFilePath = filePath || fileName || '';
  const isPDF = detectedFilePath.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(detectedFilePath);
  const isModal = variant === 'modal';
  const defaultPdfScale = pdfInitialScale === 'page-width'
    ? SpecialZoomLevel.PageWidth
    : SpecialZoomLevel.PageFit;

  /**
   * Korrekturen gibt es für Bilder und für PDFs, die nur ein Bild enthalten
   * (Scan ohne Textebene). Bei Text-PDFs bringen Filter nichts und würden das
   * Schriftbild nur verschlechtern.
   */
  const canAdjust = isImage || (isPDF && isScanPdf === true);
  /**
   * Dauerhaft speichern geht bei Bildern und bei einseitigen Scan-PDFs --
   * mehrseitige oder textbehaftete PDFs lehnt auch das Backend ab.
   */
  const canSave = !readOnly && (isImage || (isPDF && isScanPdf === true && pdfPageCount === 1));
  const adjustmentsActive = !sameAdjustments(adjustments, NEUTRAL_ADJUSTMENTS);

  /** CSS-Filterkette; die Reihenfolge entscheidet (Helligkeit vor Schwellwert). */
  const filterCss = useMemo(() => {
    if (!adjustmentsActive) return '';
    // Im Auto-Modus liegt das Ergebnis bei Bildern bereits als Pixel vor.
    // Fuer Scan-PDFs gibt es keine Canvas-Vorschau, dort naehert die
    // Filterkette den Dokumentenmodus wenigstens an.
    if (adjustments.auto) {
      if (isImage) return adjustments.invert ? 'invert(1)' : '';
      return `brightness(118%) contrast(120%) grayscale(1) url(#${THRESHOLD_FILTER_ID})${
        adjustments.invert ? ' invert(1)' : ''
      }`;
    }
    const parts = [
      `brightness(${adjustments.brightness}%)`,
      `contrast(${adjustments.contrast}%)`,
    ];
    if (adjustments.grayscale || adjustments.threshold) parts.push('grayscale(1)');
    else if (adjustments.saturate !== 100) parts.push(`saturate(${adjustments.saturate}%)`);
    if (adjustments.threshold) parts.push(`url(#${THRESHOLD_FILTER_ID})`);
    if (adjustments.invert) parts.push('invert(1)');
    return parts.join(' ');
  }, [adjustments, adjustmentsActive, isImage]);

  /** Der SVG-Schwellwertfilter wird auch von der PDF-Naeherung gebraucht. */
  const needsThresholdFilter = adjustments.threshold || (adjustments.auto && !isImage);

  const activePresetKey =
    ADJUSTMENT_PRESETS.find(preset => sameAdjustments(preset.values, adjustments))?.key ?? null;

  const setAdjustment = <K extends keyof Adjustments>(key: K, value: Adjustments[K]) =>
    setAdjustments(current => ({ ...current, [key]: value }));

  /**
   * Prüft beim Laden eines PDF, ob es eine Textebene hat. Ein paar Zeichen
   * können auch in Scans stecken (Stempel-OCR), deshalb eine kleine Toleranz.
   */
  const handlePdfLoaded = async (e: { doc: any }) => {
    try {
      const doc = e.doc;
      setPdfPageCount(doc.numPages || 0);
      const pagesToCheck = Math.min(doc.numPages || 1, 3);
      let charCount = 0;
      for (let pageNumber = 1; pageNumber <= pagesToCheck; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        const textContent = await page.getTextContent();
        charCount += textContent.items
          .map((item: any) => item.str || '')
          .join('')
          .replace(/\s/g, '').length;
        if (charCount > 50) break;
      }
      setIsScanPdf(charCount <= 50);
    } catch (err) {
      console.warn('Textebene des PDF konnte nicht geprüft werden:', err);
      setIsScanPdf(false);
    }
  };

  // Touch handlers for pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchStart({ distance });
      setInitialScale(scale);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStart) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scaleChange = distance / touchStart.distance;
      const newScale = Math.max(0.5, Math.min(3, initialScale * scaleChange));
      setScale(newScale);
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
  };

  // Reset zoom
  const resetZoom = () => {
    setScale(1.0);
  };

  useEffect(() => {
    setRotation(0);
    setAdjustments(NEUTRAL_ADJUSTMENTS);
    setShowAdjustments(false);
    setIsScanPdf(null);
    setPdfPageCount(0);
    setAutoPreviewUrl(null);
    setSaveError(null);
    setConfirmSave(false);
    loadDocument();
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentId, sourceUrl]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(sourceUrl || `/api/documents/download/${documentId}`, {
        responseType: 'blob',
        headers: {
          'Accept': isPDF ? 'application/pdf' : 'image/*'
        }
      });
      
      const blob = response.data;
      setDownloadContentType(blob.type || response.headers['content-type'] || '');
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
    } catch (err: any) {
      console.error('Error loading document:', err);
      setError(`Dokument konnte nicht geladen werden: ${err.message || 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  const changeScale = (delta: number) => {
    setScale(prevScale => {
      const newScale = prevScale + delta;
      if (newScale < 0.5) return 0.5;
      if (newScale > 3) return 3;
      return newScale;
    });
  };

  const downloadDocument = () => {
    if (documentUrl) {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = getDownloadFileName(currentFileName, downloadContentType);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSaveName = async () => {
    const newName = editedName.trim();
    if (!newName || newName === currentFileName) {
      setIsEditingName(false);
      setEditedName(currentFileName);
      return;
    }
    try {
      await apiClient.put(`/api/documents/${documentId}`, { name: newName });
      setCurrentFileName(newName);
      setIsEditingName(false);
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (err) {
      console.error('Error updating document name:', err);
      setEditedName(currentFileName);
      setIsEditingName(false);
    }
  };

  /** Rechnet die Auto-S/W-Vorschau, sobald der Modus aktiv ist (nur Bilder). */
  useEffect(() => {
    if (!adjustments.auto || !isImage || !documentUrl) {
      setAutoPreviewUrl(null);
      return;
    }

    let aborted = false;
    setAutoPreviewPending(true);
    const image = new Image();
    image.onload = () => {
      if (aborted) return;
      try {
        setAutoPreviewUrl(renderScannerBw(image, false));
      } catch (err) {
        console.error('Auto-S/W-Vorschau fehlgeschlagen:', err);
        setAutoPreviewUrl(null);
      } finally {
        setAutoPreviewPending(false);
      }
    };
    image.onerror = () => {
      if (!aborted) setAutoPreviewPending(false);
    };
    image.src = documentUrl;

    return () => {
      aborted = true;
    };
  }, [adjustments.auto, isImage, documentUrl]);

  /**
   * Speichert die Korrekturen dauerhaft: das Backend ersetzt die Datei und
   * erneuert das Thumbnail -- derselbe Weg wie beim Zuschneiden.
   */
  const handleSaveAdjustments = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await apiClient.post(`/api/documents/enhance/${documentId}`, adjustments);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Unbekannter Fehler');
      }
      setAdjustments(NEUTRAL_ADJUSTMENTS);
      setAutoPreviewUrl(null);
      setConfirmSave(false);
      setShowAdjustments(false);
      await loadDocument();
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (err: any) {
      console.error('Speichern der Bildkorrektur fehlgeschlagen:', err);
      setSaveError(err.response?.data?.error || err.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleCrop = async (cropArea: CropArea) => {
    setCropLoading(true);
    try {
      const response = await apiClient.post(`/api/documents/crop/${documentId}`, {
        x: cropArea.x,
        y: cropArea.y,
        width: cropArea.width,
        height: cropArea.height,
      });

      if (response.data.success) {
        setShowCropper(false);
        // Lade das Bild neu
        await loadDocument();
        // Benachrichtige Parent-Komponente
        if (onDocumentUpdated) {
          onDocumentUpdated();
        }
      }
    } catch (err: any) {
      console.error('Crop-Fehler:', err);
      alert(err.response?.data?.error || 'Fehler beim Zuschneiden des Bildes');
    } finally {
      setCropLoading(false);
    }
  };

  const viewer = (
    <div
      className={`fixed inset-0 z-[1200] bg-black/75 ${
        isModal ? 'flex items-center justify-center p-3 sm:p-4' : 'flex flex-col'
      }`}
      onMouseDown={isModal ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={`Dokumentvorschau ${currentFileName}`}
    >
      <div
        className={isModal
          ? 'relative flex max-h-[92dvh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-800'
          : 'relative flex h-full min-h-0 w-full flex-col'
        }
        onMouseDown={isModal ? (event) => event.stopPropagation() : undefined}
      >
      {/* Header */}
      <div className="border-b bg-white px-3 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 group">
            <div className="flex-shrink-0">
              {isPDF ? (
                <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-error-500" />
              ) : (
                <PhotoIcon className="h-5 w-5 sm:h-6 sm:w-6 text-info-500" />
              )}
            </div>
            {isEditingName && !readOnly ? (
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setEditedName(currentFileName);
                    }
                  }}
                  className="flex-1 min-w-0 px-2 py-1 text-sm sm:text-lg font-semibold bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  onClick={handleSaveName}
                  className="p-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex-shrink-0"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setEditedName(currentFileName);
                  }}
                  className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : readOnly ? (
              <h2 className="truncate text-sm font-semibold text-gray-900 sm:text-lg dark:text-white">
                {currentFileName}
              </h2>
            ) : (
              <button
                onClick={() => setIsEditingName(true)}
                className="flex items-center gap-1.5 min-w-0 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Dateiname bearbeiten"
              >
                <h2 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {currentFileName}
                </h2>
                <PencilIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Zoom Controls for Images */}
            {isImage && documentUrl && (
              <>
                <button
                  onClick={() => changeScale(-0.1)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Verkleinern"
                >
                  <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 min-w-[45px] sm:min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => changeScale(0.1)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Vergrößern"
                >
                  <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>

                <button
                  type="button"
                  onClick={() => setRotation(current => (current + 90) % 360)}
                  className="rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Bild um 90° drehen"
                  aria-label="Bild um 90 Grad drehen"
                >
                  <ArrowPathIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>

                {/* Crop-Button */}
                {!readOnly && (
                  <button
                    onClick={() => setShowCropper(true)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Bild zuschneiden"
                  >
                    <ScissorsIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                )}
              </>
            )}

            {/* Reset Zoom for Images on Mobile */}
            {isImage && documentUrl && isMobile && scale !== 1 && (
              <button
                onClick={resetZoom}
                className="p-1.5 bg-info-100 dark:bg-info-900/30 hover:bg-info-200 dark:hover:bg-info-900/50 rounded-lg transition-colors"
                title="Zoom zurücksetzen"
              >
                <span className="text-xs font-medium text-info-700 dark:text-info-300">100%</span>
              </button>
            )}

            {/* Bildkorrektur - für Bilder und Bild-PDFs */}
            {canAdjust && documentUrl && (
              <button
                type="button"
                onClick={() => setShowAdjustments(open => !open)}
                aria-pressed={showAdjustments}
                className={`relative rounded-lg p-2 transition-colors ${
                  showAdjustments || adjustmentsActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
                title="Bild verbessern (Farbfilter, Graustufen, Kontrast)"
                aria-label="Bild verbessern"
              >
                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                {adjustmentsActive && (
                  <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary-600 dark:bg-primary-400" />
                )}
              </button>
            )}

            {/* Download - Hidden on Mobile for space */}
            {!isMobile && allowDownload && (
              <button
                onClick={downloadDocument}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}

            {/* Close - Always visible, bigger on mobile */}
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              title="Schließen"
            >
              <XMarkIcon className="h-6 w-6 sm:h-5 sm:w-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className={`min-h-0 flex-1 overflow-auto bg-gray-100 p-2 dark:bg-gray-900 sm:p-4 ${
          isModal ? 'sm:bg-gray-100 sm:dark:bg-gray-900' : ''
        }`}
        onTouchStart={isImage ? handleTouchStart : undefined}
        onTouchMove={isImage ? handleTouchMove : undefined}
        onTouchEnd={isImage ? handleTouchEnd : undefined}
      >
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Dokument...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <DocumentTextIcon className="h-12 w-12 text-error-500 mx-auto mb-4" />
              <p className="text-error-600 dark:text-error-400">{error}</p>
              <button
                onClick={loadDocument}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        )}

        {!loading && !error && documentUrl && (
          <div className="flex justify-center h-full">
            {isPDF ? (
              // PDF Viewer using @react-pdf-viewer/core
              <div
                className={isModal
                  ? 'h-[65dvh] min-h-[360px] max-h-[75vh] w-full overflow-auto border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-950 sm:min-h-[500px]'
                  : 'h-full w-full'
                }
                style={filterCss ? { filter: filterCss } : undefined}
              >
                <Worker workerUrl="/pdfjs/pdf.worker.min.js">
                  <Viewer
                    fileUrl={documentUrl}
                    defaultScale={defaultPdfScale}
                    onDocumentLoad={handlePdfLoaded}
                  />
                </Worker>
              </div>
            ) : isImage ? (
              <div className="flex items-center justify-center h-full w-full">
                <div
                  className="relative flex h-full w-full items-center justify-center overflow-auto"
                  style={{
                    touchAction: scale > 1 ? 'none' : 'auto'
                  }}
                >
                  <img
                    src={autoPreviewUrl || documentUrl}
                    alt={currentFileName}
                    className="max-h-full max-w-full rounded-lg object-contain shadow-lg transition-transform duration-200"
                    style={{
                      transform: `rotate(${rotation}deg) scale(${scale})`,
                      transformOrigin: 'center',
                      cursor: scale > 1 ? 'move' : 'default',
                      filter: filterCss || undefined,
                    }}
                    draggable={false}
                  />
                  {/* Zoom Indicator on Mobile */}
                  {isMobile && scale !== 1 && (
                    <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded-lg text-xs">
                      {Math.round(scale * 100)}%
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  Vorschau für diesen Dateityp nicht verfügbar
                </p>
                {allowDownload && (
                  <button
                    onClick={downloadDocument}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Dokument herunterladen
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Schwellwert-Filter für den harten S/W-Dokumentmodus */}
      {needsThresholdFilter && (
        <svg aria-hidden="true" focusable="false" className="pointer-events-none absolute h-0 w-0">
          <filter id={THRESHOLD_FILTER_ID} colorInterpolationFilters="sRGB">
            <feComponentTransfer>
              <feFuncR type="discrete" tableValues="0 1" />
              <feFuncG type="discrete" tableValues="0 1" />
              <feFuncB type="discrete" tableValues="0 1" />
            </feComponentTransfer>
          </filter>
        </svg>
      )}

      {/* Korrektur-Panel: mobil als Slide-up ueber die volle Breite */}
      {canAdjust && showAdjustments && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center sm:p-4">
        <div className="pointer-events-auto w-full animate-slide-up border-t border-gray-200 bg-white px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl dark:border-gray-700 dark:bg-gray-800 sm:w-[34rem] sm:max-w-full sm:rounded-2xl sm:border">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white">Bild verbessern</h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setAdjustments(NEUTRAL_ADJUSTMENTS)}
                disabled={!adjustmentsActive}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
                Zurücksetzen
              </button>
              <button
                type="button"
                onClick={() => setShowAdjustments(false)}
                className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                aria-label="Korrekturen schließen"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Voreinstellungen */}
          <div className="-mx-1 mb-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
            {ADJUSTMENT_PRESETS.map(preset => (
              <button
                key={preset.key}
                type="button"
                onClick={() => setAdjustments(preset.values)}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  activePresetKey === preset.key
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Feinjustierung */}
          <div className="space-y-2">
            <AdjustSlider
              label="Helligkeit"
              min={20}
              max={200}
              value={adjustments.brightness}
              disabled={adjustments.auto}
              onChange={value => setAdjustment('brightness', value)}
            />
            <AdjustSlider
              label="Kontrast"
              min={20}
              max={300}
              value={adjustments.contrast}
              disabled={adjustments.auto}
              onChange={value => setAdjustment('contrast', value)}
            />
            <AdjustSlider
              label="Sättigung"
              min={0}
              max={250}
              value={adjustments.saturate}
              disabled={adjustments.auto || adjustments.grayscale || adjustments.threshold}
              onChange={value => setAdjustment('saturate', value)}
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {([
              { key: 'grayscale' as const, label: 'Graustufen' },
              { key: 'threshold' as const, label: 'Nur S/W' },
              { key: 'invert' as const, label: 'Invertieren' },
            ]).map(toggle => (
              <button
                key={toggle.key}
                type="button"
                aria-pressed={adjustments[toggle.key]}
                disabled={adjustments.auto && toggle.key !== 'invert'}
                onClick={() => setAdjustment(toggle.key, !adjustments[toggle.key])}
                className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 ${
                  adjustments[toggle.key]
                    ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {toggle.label}
              </button>
            ))}
          </div>

          {adjustments.auto && (
            <p className="mt-2.5 rounded-lg bg-primary-50 px-2.5 py-2 text-[11px] leading-relaxed text-primary-800 dark:bg-primary-900/25 dark:text-primary-200">
              {autoPreviewPending
                ? 'Automatischer Dokumentenmodus wird berechnet …'
                : isImage
                  ? 'Automatischer Dokumentenmodus: Schatten werden ausgeglichen, der Schwellwert wird aus dem Bild berechnet.'
                  : 'Für Scan-PDFs zeigt die Vorschau nur eine Näherung. Beim Speichern rechnet der Server den vollen Dokumentenmodus.'}
            </p>
          )}

          {saveError && (
            <p className="mt-2.5 rounded-lg bg-error-50 px-2.5 py-2 text-[11px] font-semibold text-error-700 dark:bg-error-900/25 dark:text-error-300">
              {saveError}
            </p>
          )}

          {canSave ? (
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-200 pt-2.5 dark:border-gray-700">
              {confirmSave ? (
                <>
                  <p className="min-w-0 flex-1 text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                    Original dauerhaft ersetzen?
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfirmSave(false)}
                    disabled={saving}
                    className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAdjustments}
                    disabled={saving}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-60"
                  >
                    {saving ? 'Speichert …' : 'Ja, ersetzen'}
                  </button>
                </>
              ) : (
                <>
                  <p className="min-w-0 flex-1 text-[10.5px] leading-relaxed text-gray-400 dark:text-gray-500">
                    Speichern ersetzt die Datei und erneuert das Vorschaubild.
                  </p>
                  <button
                    type="button"
                    onClick={() => setConfirmSave(true)}
                    disabled={!adjustmentsActive || autoPreviewPending}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-primary-700 disabled:opacity-40"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Speichern
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="mt-2.5 text-[10.5px] leading-relaxed text-gray-400 dark:text-gray-500">
              {isPDF
                ? 'Mehrseitige PDFs lassen sich nur ansehen, nicht dauerhaft aufbereiten.'
                : 'Die Korrekturen wirken nur in dieser Ansicht.'}
            </p>
          )}
        </div>
        </div>
      )}

      {isModal && (
        <div className="flex items-center justify-between gap-3 border-t bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
          <p className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
            Datei: {currentFileName}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            Schließen
          </button>
        </div>
      )}

      {/* Image Cropper Modal */}
      {showCropper && documentUrl && (
        <ImageCropper
          imageUrl={documentUrl}
          onCrop={handleCrop}
          onCancel={() => setShowCropper(false)}
          loading={cropLoading}
        />
      )}
      </div>
    </div>
  );

  // Der Viewer kann aus verschachtelten Modals geöffnet werden. Ohne Portal
  // bleibt `position: fixed` im transformierten Stapelkontext des Elternmodals
  // gefangen und liegt trotz hohem z-index hinter dem nächsten Dialog.
  return createPortal(viewer, document.body);
};

export default DocumentViewer;
